/**
 * Reconhecimento das chaves que o MSAL grava no `localStorage`.
 *
 * Motivo de existir: o logout limpava o cache do MSAL filtrando por PREFIXO (`msal`,
 * `login.windows.net`, `adal`), mas o MSAL v3 **não** prefixa as entidades — a chave começa com o id
 * da conta:
 *
 *   <uid>.<utid>-login.windows.net-refreshtoken-<clientId>--
 *   <uid>.<utid>-login.windows.net-accesstoken-<clientId>-<tid>-<scopes>
 *   <uid>.<utid>-login.windows.net-idtoken-<clientId>-<tid>-
 *   <uid>.<utid>-login.windows.net-<tenantId>            (a conta)
 *
 * Nenhuma dessas casa com `startsWith('msal')` ou `startsWith('login.windows.net')`. O filtro antigo
 * removia só os MAPAS (`msal.account.keys`, `msal.token.keys.<clientId>`) — e o MSAL os reconstrói
 * sozinho no próximo boot varrendo o storage (`createKeyMaps`, ligado por `cacheLocation:
 * 'localStorage'`). Resultado: a conta e o refresh token sobreviviam ao "Sair" e o app se
 * reautenticava em silêncio.
 *
 * A função é pura de propósito, para ser testável sem storage nem MSAL.
 */

/** Marca de "o usuário clicou em Sair" — impede a reautenticação silenciosa no boot seguinte. */
export const INTENTIONAL_LOGOUT_KEY = 'erp_intentional_logout';

/** Trechos que aparecem no MEIO das chaves de credencial do MSAL v3. */
const MSAL_KEY_FRAGMENTS = [
  'login.windows.net',
  'login.microsoftonline.com',
  '-accesstoken-',
  '-refreshtoken-',
  '-idtoken-',
];

/** Prefixos usados pelos mapas de chave do MSAL e pelo ADAL legado. */
const MSAL_KEY_PREFIXES = ['msal', 'adal'];

/**
 * A chave pertence ao cache do MSAL/ADAL (e portanto deve sair no logout)?
 *
 * Nunca deve casar com chaves da própria aplicação (filtros de tela, preferências), que precisam
 * sobreviver ao logout.
 */
export const isMsalCacheKey = (key: string): boolean => {
  if (!key) return false;
  if (key === INTENTIONAL_LOGOUT_KEY) return false;

  const lower = key.toLowerCase();
  return (
    MSAL_KEY_PREFIXES.some((prefix) => lower.startsWith(prefix)) ||
    MSAL_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment))
  );
};
